/**
 * ============================================================================
 * EVOLIS — module assistant IA (développement Louis, conçu pour être détachable)
 *  Tout le module frontend vit dans src/evolis/. Points d'ancrage dans le
 *  reste du site : la route « /evolis » d'App.js et le bouton de Sidebar.js
 *  (lignes marquées « EVOLIS »). Voir EvoluWeb/EVOLIS-MODULE.md.
 *
 *  Ce fichier est le CŒUR du chat, réutilisé par la page (EvolisPage) et le
 *  dock flottant (EvolisDock). Il gère l'envoi, le chargement d'une
 *  conversation sauvegardée, et le rendu (texte + graphiques ```chart```).
 * ============================================================================
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import authService from '../services/auth.service';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { SendHorizonal, Building2, Globe2, Download, Table2, Square, RotateCcw } from 'lucide-react';
import { exportRowsToExcel } from '../utils/exportRowsToExcel';
import EvolisLogo from './EvolisLogo';
// Suggestions par défaut ; le dock flottant passe des suggestions adaptées
// à la page où il est ouvert (voir contexte-pages.js).
import { SUGGESTIONS_GENERIQUES } from './contexte-pages';
import './evolis.css';

// Accès au module : permission « access_evolis » (rôle ∪ grade ∪ extras).
export const accesEvolis = () => authService.hasPermission('access_evolis');

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const PALETTE = ['#6366f1', '#22c55e', '#f97316', '#3b82f6', '#ec4899', '#14b8a6', '#eab308', '#8b5cf6', '#ef4444', '#06b6d4'];
const AXIS_COLOR = '#64748b';
const GRID_COLOR = 'rgba(148, 163, 184, 0.18)';

/** Extrait les éventuels blocs ```chart``` et ```tableau``` d'une réponse (stockée brute). */
const parseReponse = (texte) => {
    const matchChart = texte.match(/```chart\s*([\s\S]*?)```/);
    let chart = null;
    if (matchChart) {
        try {
            const spec = JSON.parse(matchChart[1]);
            if (Array.isArray(spec.labels) && Array.isArray(spec.valeurs) && spec.labels.length > 0) {
                chart = spec;
            }
        } catch { /* bloc malformé : on affiche juste le texte */ }
    }
    const matchTableau = texte.match(/```tableau\s*([\s\S]*?)```/);
    let tableau = null;
    if (matchTableau) {
        try {
            const spec = JSON.parse(matchTableau[1]);
            if (Array.isArray(spec.colonnes) && Array.isArray(spec.lignes) && spec.colonnes.length > 0 && spec.lignes.length > 0) {
                tableau = spec;
            }
        } catch { /* bloc malformé : on affiche juste le texte */ }
    }
    return { texte: texte.replace(/```(chart|tableau)[\s\S]*?```/g, '').trim(), chart, tableau };
};

/**
 * Lien interne vers une fiche SPOT (ex : /admin/projets?open=12), fourni par
 * les outils backend. Navigation SPA, jamais de rechargement de page.
 * Seuls les chemins internes (commençant par « / ») sont rendus cliquables.
 */
const LienInterne = ({ vers, children }) => {
    const navigate = useNavigate();
    return (
        <a
            href={vers}
            className="evolis-lien"
            onClick={(e) => { e.preventDefault(); navigate(vers); }}
        >
            {children}
        </a>
    );
};

/** Rendu inline : [liens internes](/...), `code`, **gras**, *italique*. */
const renduInline = (texte, cle = 'i') => {
    let m;
    if ((m = texte.match(/\[([^\]\n]+)\]\(\s*(\/[^)\s]+)\s*\)/))) {
        return (
            <React.Fragment key={cle}>
                {renduInline(texte.slice(0, m.index), cle + 'a')}
                <LienInterne vers={m[2]}>{renduInline(m[1], cle + 'l')}</LienInterne>
                {renduInline(texte.slice(m.index + m[0].length), cle + 'b')}
            </React.Fragment>
        );
    }
    if ((m = texte.match(/`([^`]+)`/))) {
        return (
            <React.Fragment key={cle}>
                {renduInline(texte.slice(0, m.index), cle + 'a')}
                <code>{m[1]}</code>
                {renduInline(texte.slice(m.index + m[0].length), cle + 'b')}
            </React.Fragment>
        );
    }
    if ((m = texte.match(/\*\*(.+?)\*\*/))) {
        return (
            <React.Fragment key={cle}>
                {renduInline(texte.slice(0, m.index), cle + 'a')}
                <strong>{renduInline(m[1], cle + 's')}</strong>
                {renduInline(texte.slice(m.index + m[0].length), cle + 'b')}
            </React.Fragment>
        );
    }
    if ((m = texte.match(/(^|[^*])\*([^*\n]+)\*/))) {
        const debut = m.index + m[1].length;
        return (
            <React.Fragment key={cle}>
                {renduInline(texte.slice(0, debut), cle + 'a')}
                <em>{renduInline(m[2], cle + 'e')}</em>
                {renduInline(texte.slice(debut + m[0].length - m[1].length), cle + 'b')}
            </React.Fragment>
        );
    }
    return texte;
};

/**
 * Rendu du markdown courant produit par le modèle : titres, listes (puces et
 * numérotées), tableaux, paragraphes, gras/italique/code. Tout ce qui n'est
 * pas reconnu s'affiche en texte simple — plus jamais de caractères bruts.
 */
const RenduTexte = ({ texte }) => {
    const lignes = texte.split('\n');
    const elements = [];
    let i = 0;

    while (i < lignes.length) {
        const brute = lignes[i].trim();

        // Lignes vides et séparateurs horizontaux (---, ***) : ignorés.
        if (brute === '' || /^[-*_]{3,}$/.test(brute)) { i++; continue; }

        // Titres markdown (#, ##, ###...) → intertitre sobre.
        if (/^#{1,5}\s+/.test(brute)) {
            elements.push(<div key={i} className="evolis-titre">{renduInline(brute.replace(/^#{1,5}\s+/, ''))}</div>);
            i++;
            continue;
        }

        // Tableaux markdown | a | b | → vrai tableau.
        if (/^\|.*\|$/.test(brute)) {
            const rangees = [];
            while (i < lignes.length && /^\|.*\|$/.test(lignes[i].trim())) {
                const t = lignes[i].trim();
                if (!/^\|[\s\-:|]+\|$/.test(t)) {
                    rangees.push(t.slice(1, -1).split('|').map((c) => c.trim()));
                }
                i++;
            }
            if (rangees.length > 0) {
                const [entete, ...corps] = rangees;
                elements.push(
                    <table key={i} className="evolis-table">
                        <thead>
                            <tr>{entete.map((c, ci) => <th key={ci}>{renduInline(c, `h${ci}`)}</th>)}</tr>
                        </thead>
                        <tbody>
                            {corps.map((r, ri) => (
                                <tr key={ri}>{r.map((c, ci) => <td key={ci}>{renduInline(c, `c${ri}-${ci}`)}</td>)}</tr>
                            ))}
                        </tbody>
                    </table>
                );
            }
            continue;
        }

        // Listes : puces (-, •, *) ou numérotées (1. / 1)).
        const estPuce = (t) => /^[-•*]\s+/.test(t);
        const estNum = (t) => /^\d+[.)]\s+/.test(t);
        if (estPuce(brute) || estNum(brute)) {
            const ordonnee = estNum(brute);
            const items = [];
            while (i < lignes.length && (estPuce(lignes[i].trim()) || estNum(lignes[i].trim()))) {
                items.push(lignes[i].trim().replace(/^([-•*]|\d+[.)])\s+/, ''));
                i++;
            }
            const Balise = ordonnee ? 'ol' : 'ul';
            elements.push(
                <Balise key={i}>
                    {items.map((item, li) => <li key={li}>{renduInline(item, `l${li}`)}</li>)}
                </Balise>
            );
            continue;
        }

        // Paragraphe : lignes consécutives « normales », <br/> entre elles.
        const para = [];
        while (i < lignes.length) {
            const t = lignes[i].trim();
            if (t === '' || /^#{1,5}\s+/.test(t) || /^\|.*\|$/.test(t) || estPuce(t) || estNum(t) || /^[-*_]{3,}$/.test(t)) break;
            para.push(t);
            i++;
        }
        elements.push(
            <p key={i}>
                {para.map((l, li) => (
                    <React.Fragment key={li}>{li > 0 && <br />}{renduInline(l, `p${li}`)}</React.Fragment>
                ))}
            </p>
        );
    }

    return elements;
};

const GraphiqueEvolis = ({ spec }) => {
    const data = {
        labels: spec.labels,
        datasets: [{
            label: spec.titre || '',
            data: spec.valeurs,
            backgroundColor: spec.type === 'line' ? 'rgba(99, 102, 241, 0.15)' : spec.labels.map((_, i) => PALETTE[i % PALETTE.length]),
            borderColor: spec.type === 'line' ? '#6366f1' : undefined,
            borderRadius: spec.type === 'bar' ? 6 : undefined,
            fill: spec.type === 'line',
            tension: 0.35,
        }],
    };
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: spec.type === 'doughnut'
                ? { position: 'right', labels: { color: AXIS_COLOR, boxWidth: 12 } }
                : { display: false },
            title: spec.titre
                ? { display: true, text: spec.titre, color: AXIS_COLOR, font: { size: 13, weight: '600' } }
                : { display: false },
        },
        scales: spec.type === 'doughnut' ? undefined : {
            x: { ticks: { color: AXIS_COLOR }, grid: { color: GRID_COLOR } },
            y: { ticks: { color: AXIS_COLOR }, grid: { color: GRID_COLOR }, beginAtZero: true },
        },
    };
    const Composant = spec.type === 'doughnut' ? Doughnut : spec.type === 'line' ? Line : Bar;
    return (
        <div className="evolis-chart">
            <Composant data={data} options={options} />
        </div>
    );
};

/**
 * Tableau de données généré par Evolis (bloc ```tableau```), avec export Excel
 * en un clic via la brique commune du site (exportRowsToExcel / exceljs).
 * Les données viennent exclusivement des outils backend.
 */
const TableauEvolis = ({ spec }) => {
    // Colonnes acceptées sous deux formes : "Nom" ou {"nom":"...","type":"nombre"}
    const colonnes = spec.colonnes.map((c) =>
        typeof c === 'string' ? { nom: c, type: 'texte' } : { nom: String(c?.nom ?? ''), type: c?.type || 'texte' }
    );
    const lignes = spec.lignes.filter(Array.isArray);

    const fmt = (v, type) => {
        if (v === null || v === undefined || v === '') return '—';
        if (type === 'nombre') {
            const n = typeof v === 'number' ? v : parseFloat(v);
            return Number.isFinite(n) ? n.toLocaleString('fr-FR') : String(v);
        }
        return String(v);
    };

    const telecharger = () => {
        const columns = colonnes.map((c, i) => ({
            header: c.nom,
            key: `c${i}`,
            width: Math.max(14, Math.min(34, c.nom.length + 8)),
            type: c.type === 'nombre' ? 'number' : c.type === 'date' ? 'date' : 'text',
        }));
        const rows = lignes.map((l) => Object.fromEntries(l.map((v, i) => [`c${i}`, v])));
        const nom = String(spec.fichier || spec.titre || 'export-evolis')
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-').toLowerCase() || 'export-evolis';
        exportRowsToExcel({
            sheetName: 'Evolis',
            fileName: nom,
            columns,
            rows,
            title: spec.titre || undefined,
        });
    };

    return (
        <div className="evolis-tableau">
            <div className="evolis-tableau-entete">
                <div className="evolis-tableau-id">
                    <span className="evolis-tableau-icone"><Table2 size={14} /></span>
                    <div className="evolis-tableau-titres">
                        <span className="evolis-tableau-titre">{spec.titre || 'Tableau'}</span>
                        <span className="evolis-tableau-sous">
                            {lignes.length} ligne{lignes.length > 1 ? 's' : ''} · {colonnes.length} colonnes
                        </span>
                    </div>
                </div>
                <button type="button" className="evolis-tableau-dl" onClick={telecharger} title="Télécharger en Excel (.xlsx)">
                    <Download size={13} /> Excel
                </button>
            </div>
            <div className="evolis-tableau-scroll">
                <table className="evolis-tableau-grille">
                    <thead>
                        <tr>
                            {colonnes.map((c, i) => (
                                <th key={i} className={c.type === 'nombre' ? 'evolis-td-num' : undefined}>{c.nom}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {lignes.map((l, ri) => (
                            <tr key={ri}>
                                {colonnes.map((c, ci) => (
                                    <td key={ci} className={c.type === 'nombre' ? 'evolis-td-num' : undefined}>
                                        {fmt(l[ci], c.type)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// Effet « machine à écrire » : vitesse de révélation du texte (~110 caractères/s).
const ANIM_CHARS_PAR_TICK = 2;
const ANIM_TICK_MS = 18;

/**
 * Une bulle de message. Les réponses d'Evolis sont stockées brutes et parsées ici.
 * Si `message.anime` est vrai (réponse fraîchement reçue), le texte s'écrit
 * progressivement façon ChatGPT ; le graphique apparaît à la fin.
 */
const MessageEvolis = ({ message, onFinAnimation, doitSuivre, onReessayer }) => {
    const estBot = message.role === 'assistant';
    // Pendant le streaming, un bloc ```chart peut être ouvert mais pas encore
    // fermé : on le masque jusqu'à ce qu'il soit complet (sinon JSON brut).
    let contenu = message.content;
    if (message.streaming) {
        const debutBloc = contenu.indexOf('```');
        if (debutBloc !== -1 && contenu.indexOf('```', debutBloc + 3) === -1) {
            contenu = contenu.slice(0, debutBloc);
        }
    }
    const { texte, chart, tableau } = estBot ? parseReponse(contenu) : { texte: contenu, chart: null, tableau: null };
    const [nbVisibles, setNbVisibles] = useState((message.anime || message.streaming) ? 0 : Infinity);
    const bulleRef = useRef(null);

    // Machine à écrire lissée par-dessus le streaming : les tokens Mistral
    // arrivent par fragments irréguliers, on les révèle à cadence régulière.
    // Si le flux va plus vite que l'affichage, la cadence accélère pour ne
    // jamais laisser le retard s'accumuler ; si l'affichage rattrape le flux,
    // il attend simplement les tokens suivants. Les refs évitent de recréer
    // le minuteur à chaque token reçu.
    const texteRef = useRef(texte);
    texteRef.current = texte;
    const streamingRef = useRef(!!message.streaming);
    streamingRef.current = !!message.streaming;
    const animerRef = useRef(!!(message.anime || message.streaming));

    useEffect(() => {
        if (!animerRef.current) return;
        const timer = setInterval(() => {
            setNbVisibles((n) => {
                const total = texteRef.current.length;
                if (n >= total) {
                    // À jour. Flux encore ouvert : on attend les prochains tokens.
                    if (streamingRef.current) return n;
                    clearInterval(timer);
                    onFinAnimation?.();
                    return Infinity;
                }
                // Rattrapage adouci : on accélère si le flux prend de l'avance,
                // mais jamais au point de perdre l'effet d'écriture.
                const retard = total - n;
                const pas = retard > 600 ? 10 : retard > 200 ? 5 : ANIM_CHARS_PAR_TICK;
                return Math.min(n + pas, total);
            });
        }, ANIM_TICK_MS);
        return () => clearInterval(timer);
    }, [onFinAnimation]);

    // Suit la frappe : garde le bas du fil visible pendant l'animation, sauf si
    // l'utilisateur a repris la main sur le scroll (voir doitSuivre). On assigne
    // directement scrollTop (positionnement sec, pas d'animation) : les
    // scrollIntoView répétés s'empilaient et se battaient avec la molette de
    // l'utilisateur au moment où il revenait en bas → affichage saccadé.
    useEffect(() => {
        if (nbVisibles === Infinity) return;
        if (doitSuivre && !doitSuivre()) return;
        const fil = bulleRef.current?.closest('.evolis-thread');
        if (fil) fil.scrollTop = fil.scrollHeight;
    }, [nbVisibles, doitSuivre]);

    const enCours = estBot && nbVisibles < texte.length;
    const texteVisible = enCours ? texte.slice(0, nbVisibles) : texte;

    return (
        <div className={`evolis-msg ${estBot ? 'evolis-msg-bot' : 'evolis-msg-user'}${message.echec ? ' evolis-msg-echec' : ''}`}>
            {estBot && <span className="evolis-msg-avatar"><EvolisLogo size={13} /></span>}
            <div className="evolis-bubble" ref={bulleRef}>
                <RenduTexte texte={texteVisible} />
                {enCours && <span className="evolis-caret" aria-hidden="true" />}
                {!enCours && tableau && <TableauEvolis spec={tableau} />}
                {!enCours && chart && <GraphiqueEvolis spec={chart} />}
                {!enCours && message.echec && message.reessai && onReessayer && (
                    <button type="button" className="evolis-retry" onClick={() => onReessayer(message.reessai)}>
                        <RotateCcw size={13} /> Réessayer
                    </button>
                )}
            </div>
        </div>
    );
};

/**
 * Cœur du chat Evolis.
 *
 * @param conversationId  id de la conversation à poursuivre (null = nouvelle)
 * @param onConversation  callback(id) quand le backend crée/confirme la conversation
 * @param onTitre         callback(id, titre) quand le titre automatique est généré
 * @param compact         true pour le dock flottant (entête réduite)
 * @param contextePage    libellé de la page courante, transmis à l'IA
 * @param suggestions     questions proposées sur l'écran d'accueil (adaptées
 *                        à la page courante pour le dock flottant)
 * @param enteteExtras    boutons supplémentaires à droite de l'entête (dock)
 */
const EvolisChat = ({ conversationId = null, onConversation, onTitre, compact = false, contextePage = 'Evolis', suggestions = SUGGESTIONS_GENERIQUES, enteteExtras = null }) => {
    const user = authService.getCurrentUser();
    // Portée « toutes les entités » : permission evolis_toutes_entites
    // (extras fiche ou grade, réglée dans le DevTool).
    const peutToutesEntites = authService.hasUserPermission('evolis_toutes_entites');

    const [messages, setMessages] = useState([]);
    const [saisie, setSaisie] = useState('');
    const [chargement, setChargement] = useState(false);
    // Étape en cours annoncée par le backend pendant le flux (« Calcul : CA par entité… »)
    const [etape, setEtape] = useState('');
    const [portee, setPortee] = useState('entite');
    const [erreur, setErreur] = useState('');
    const [nonConfigure, setNonConfigure] = useState(false);
    const finRef = useRef(null);
    const inputRef = useRef(null);
    const filRef = useRef(null);
    // Contrôle d'annulation du flux en cours (bouton Stop façon ChatGPT).
    const abortRef = useRef(null);
    // id de la conversation actuellement affichée (évite de recharger celle
    // qu'on vient de créer quand le parent nous repasse son id en prop).
    const idAffiche = useRef(null);
    // Suivi automatique du scroll : coupé dès que l'utilisateur remonte dans le
    // fil (molette, tactile), réactivé quand il revient de lui-même en bas.
    const suiviAuto = useRef(true);

    useEffect(() => {
        const fil = filRef.current;
        if (!fil) return;
        const surIntention = (e) => {
            if (e.type !== 'wheel' || e.deltaY < 0) suiviAuto.current = false;
        };
        let dernierScrollTop = fil.scrollTop;
        const surScroll = () => {
            // Le suivi automatique ne fait jamais remonter le fil : toute
            // remontée est donc une action de l'utilisateur (barre de défilement).
            if (fil.scrollTop < dernierScrollTop - 2) suiviAuto.current = false;
            dernierScrollTop = fil.scrollTop;
            // Réengagement uniquement une fois VRAIMENT en bas : le suivi reprend
            // alors d'un coup net, sans se battre avec l'inertie de la molette.
            const distanceBas = fil.scrollHeight - fil.scrollTop - fil.clientHeight;
            if (distanceBas < 16) suiviAuto.current = true;
        };
        fil.addEventListener('wheel', surIntention, { passive: true });
        fil.addEventListener('touchmove', surIntention, { passive: true });
        fil.addEventListener('scroll', surScroll, { passive: true });
        return () => {
            fil.removeEventListener('wheel', surIntention);
            fil.removeEventListener('touchmove', surIntention);
            fil.removeEventListener('scroll', surScroll);
        };
    }, []);

    const doitSuivre = useCallback(() => suiviAuto.current, []);

    // Suivi du bas de fil : positionnement direct (pas d'animation), sinon les
    // scrolls lissés s'empilent pendant le streaming et se battent avec la molette.
    useEffect(() => {
        if (suiviAuto.current && filRef.current) filRef.current.scrollTop = filRef.current.scrollHeight;
    }, [messages, chargement, etape]);

    // Fin de l'effet machine à écrire : on retire le drapeau pour que les
    // rendus suivants (et l'historique rechargé) affichent le texte d'un bloc.
    const finAnimation = useCallback(() => {
        setMessages((prev) => (prev.some((m) => m.anime) ? prev.map((m) => (m.anime ? { ...m, anime: false } : m)) : prev));
    }, []);

    // Chargement d'une conversation sauvegardée (ou remise à zéro si null).
    // Important : idAffiche n'est marqué qu'APRÈS un chargement réussi, sinon le
    // double montage-démontage du StrictMode (dev) annule la requête au premier
    // passage et croit le travail déjà fait au second — messages jamais affichés.
    useEffect(() => {
        if (conversationId === null) {
            idAffiche.current = null;
            setMessages([]);
            setErreur('');
            return;
        }
        if (conversationId === idAffiche.current) return; // déjà affichée (id reçu après envoi)
        setErreur('');
        let annule = false;
        axios.get(`${API_URL}/assistant/conversations/${conversationId}`, { withCredentials: true })
            .then((res) => {
                if (annule) return;
                if (res.data?.ok) {
                    idAffiche.current = conversationId;
                    setMessages(res.data.conversation.messages || []);
                } else {
                    setErreur('Impossible de charger cette conversation.');
                }
            })
            .catch(() => { if (!annule) setErreur('Impossible de charger cette conversation.'); });
        return () => { annule = true; };
    }, [conversationId]);

    const arreter = useCallback(() => {
        abortRef.current?.abort();
        abortRef.current = null;
        setChargement(false);
        setEtape('');
        setMessages((prev) => {
            const dernier = prev[prev.length - 1];
            if (dernier?.streaming) {
                const texte = (dernier.content || '').trim();
                if (texte) return [...prev.slice(0, -1), { ...dernier, streaming: false }];
                return prev.slice(0, -1);
            }
            return prev;
        });
    }, []);

    const envoyer = useCallback(async (texteForce) => {
        const texte = (texteForce ?? saisie).trim();
        if (!texte || chargement) return;

        setErreur('');
        setSaisie('');
        suiviAuto.current = true; // envoyer un message ramène (et garde) le fil en bas
        // Si on réessaie, on retire l'éventuel message d'échec précédent.
        const base = messages.filter((m) => !m.echec);
        const dernier = base[base.length - 1];
        const dejaPosee = dernier?.role === 'user' && dernier.content === texte;
        const nouveauxMessages = dejaPosee ? base : [...base, { role: 'user', content: texte }];
        setMessages(nouveauxMessages);
        setChargement(true);
        setEtape('');

        const controle = new AbortController();
        abortRef.current = controle;

        const afficherEchec = (msg) => {
            setMessages((prev) => {
                const sansStream = prev.filter((m) => !m.streaming);
                return [...sansStream, {
                    role: 'assistant',
                    content: msg,
                    echec: true,
                    reessai: texte,
                    anime: true,
                }];
            });
        };

        // Traitement d'un événement du flux NDJSON envoyé par le backend.
        // La réponse arrive token par token (streaming Mistral), puis « fin »
        // la confirme ; « titre » (nom automatique) arrive encore après.
        let finRecue = false;
        const traiterEvenement = (evt) => {
            if (evt.type === 'etape') {
                setEtape(evt.texte || '');
                return false;
            }
            if (evt.type === 'token') {
                // Fragment de réponse : ajouté au message en cours de flux.
                setEtape('');
                setMessages((prev) => {
                    const dernier = prev[prev.length - 1];
                    if (dernier?.streaming) {
                        return [...prev.slice(0, -1), { ...dernier, content: dernier.content + (evt.texte || '') }];
                    }
                    return [...prev, { role: 'assistant', content: evt.texte || '', streaming: true }];
                });
                return false;
            }
            if (evt.type === 'reprise') {
                // Mistral a finalement appelé des outils : on efface le brouillon.
                setMessages((prev) => prev.filter((m) => !m.streaming));
                setEtape('Evolis consulte les données…');
                return false;
            }
            if (evt.type === 'fin') {
                finRecue = true;
                setMessages((prev) => {
                    // La version « fin » fait foi (elle contient le bloc chart complet).
                    const sansStream = prev.filter((m) => !m.streaming);
                    return [...sansStream, { role: 'assistant', content: evt.reponse || '', anime: true }];
                });
                if (evt.conversationId) {
                    idAffiche.current = evt.conversationId;
                    onConversation?.(evt.conversationId);
                }
                setChargement(false);
                setEtape('');
                inputRef.current?.focus();
                return false;
            }
            if (evt.type === 'titre') {
                if (evt.conversationId && evt.titre) onTitre?.(evt.conversationId, evt.titre);
                return false;
            }
            if (evt.type === 'erreur') {
                // Filet de sécurité : le backend préfère désormais « fin »,
                // mais on garde ce chemin pour les vieux déploiements.
                const messagesErreur = {
                    'cle-invalide': 'La clé API Mistral est invalide. Vérifie MISTRAL_API_KEY côté serveur.',
                    'mistral-injoignable': 'Le service IA est un peu chargé. Attends ~15 secondes puis réessaie.',
                    'erreur-mistral': 'Le service IA a renvoyé une erreur. Réessaie dans un instant.',
                    'trop-de-tours': 'La question est trop dense pour un seul échange. Découpe-la en 2 questions plus simples.',
                };
                afficherEchec(messagesErreur[evt.raison] || "Je n'ai pas pu terminer. Réessaie dans un instant.");
                return true;
            }
            return false;
        };

        try {
            // fetch (et pas axios) : nécessaire pour lire la réponse en flux.
            const res = await fetch(`${API_URL}/assistant/message`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                signal: controle.signal,
                body: JSON.stringify({
                    messages: nouveauxMessages.map((m) => ({ role: m.role, content: m.content })),
                    portee,
                    contexte: { page: contextePage },
                    conversationId: idAffiche.current,
                }),
            });

            const typeContenu = res.headers.get('content-type') || '';
            if (!res.ok || typeContenu.includes('application/json')) {
                // Réponses hors flux : contrôles d'accès, limite horaire, configuration.
                const data = await res.json().catch(() => null);
                if (res.status === 429) afficherEchec('Tu as atteint la limite de questions pour cette heure. Reviens un peu plus tard.');
                else if (res.status === 403) afficherEchec("Ton compte n'a pas accès à Evolis.");
                else if (data?.raison === 'non-configure') setNonConfigure(true);
                else if (data?.ok && data?.reponse) {
                    setMessages((prev) => [...prev.filter((m) => !m.streaming), { role: 'assistant', content: data.reponse, anime: true }]);
                } else afficherEchec('Evolis est momentanément indisponible. Réessaie dans un instant.');
                return;
            }

            // Lecture du flux ligne par ligne (une ligne = un événement JSON),
            // jusqu'à la fermeture côté serveur (après « fin » puis « titre »).
            const lecteur = res.body.getReader();
            const decodeur = new TextDecoder();
            let tampon = '';
            let arret = false;
            while (!arret) {
                const { done, value } = await lecteur.read();
                if (done) break;
                tampon += decodeur.decode(value, { stream: true });
                let saut;
                while ((saut = tampon.indexOf('\n')) >= 0) {
                    const ligne = tampon.slice(0, saut).trim();
                    tampon = tampon.slice(saut + 1);
                    if (!ligne) continue;
                    try {
                        if (traiterEvenement(JSON.parse(ligne))) arret = true;
                    } catch { /* ligne malformée : ignorée */ }
                }
            }
            if (!finRecue && !arret && !controle.signal.aborted) {
                afficherEchec('La connexion a été interrompue pendant la réponse. Réessaie — si ça revient souvent, découpe la question.');
            }
        } catch (e) {
            if (e?.name === 'AbortError') {
                // Arrêt volontaire : pas de message d'erreur.
            } else {
                afficherEchec('Impossible de joindre Evolis (réseau). Vérifie ta connexion et réessaie.');
            }
        } finally {
            if (abortRef.current === controle) abortRef.current = null;
            setChargement(false);
            setEtape('');
            inputRef.current?.focus();
        }
    }, [saisie, chargement, messages, portee, contextePage, onConversation, onTitre]);

    if (nonConfigure) {
        return (
            <div className={`evolis-panel${compact ? ' evolis-compact' : ''}`}>
                <div className="evolis-empty">
                    <EvolisLogo size={40} />
                    <h3>Evolis n'est pas encore activé</h3>
                    <p>
                        La clé API Mistral n'est pas configurée sur le serveur.
                        Renseigner <code>MISTRAL_API_KEY</code> dans les variables d'environnement
                        (backend), puis recharger cette page.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={`evolis-panel${compact ? ' evolis-compact' : ''}`}>
            <div className="evolis-header">
                <div className="evolis-identity">
                    <span className="evolis-avatar"><EvolisLogo size={compact ? 14 : 18} /></span>
                    <div>
                        <span className="evolis-name">Evolis</span>
                        {!compact && <span className="evolis-tagline">L'assistant IA de SPOT — il interroge vos données, jamais l'inverse</span>}
                    </div>
                </div>
                <div className="evolis-actions">
                    {peutToutesEntites && (
                        <div className="evolis-scope" title="Portée des données interrogées">
                            <button
                                className={`evolis-scope-btn${portee === 'entite' ? ' active' : ''}`}
                                onClick={() => setPortee('entite')}
                            >
                                <Building2 size={13} />{!compact && ' Mon entité'}
                            </button>
                            <button
                                className={`evolis-scope-btn${portee === 'toutes' ? ' active' : ''}`}
                                onClick={() => setPortee('toutes')}
                            >
                                <Globe2 size={13} />{!compact && ' Toutes'}
                            </button>
                        </div>
                    )}
                    {enteteExtras}
                </div>
            </div>

            <div className="evolis-thread" ref={filRef}>
                {messages.length === 0 && !chargement && (
                    <div className="evolis-welcome">
                        <EvolisLogo size={compact ? 26 : 34} />
                        <h3>Bonjour {user?.prenom || ''} !</h3>
                        <p>Pose-moi une question sur tes projets, ton CA, tes provenances… Je calcule sur les données de {portee === 'toutes' ? 'toutes les entités' : 'ton entité'}, en direct.</p>
                        <div className="evolis-suggestions">
                            {suggestions.map((s) => (
                                <button key={s} className="evolis-suggestion" onClick={() => envoyer(s)}>{s}</button>
                            ))}
                        </div>
                    </div>
                )}

                {messages.map((m, i) => (
                    <MessageEvolis
                        key={i}
                        message={m}
                        onFinAnimation={finAnimation}
                        doitSuivre={doitSuivre}
                        onReessayer={envoyer}
                    />
                ))}

                {chargement && !messages.some((m) => m.streaming) && (
                    <div className="evolis-msg evolis-msg-bot">
                        <span className="evolis-msg-avatar"><EvolisLogo size={13} /></span>
                        <div className="evolis-bubble evolis-typing">
                            {etape || 'Evolis analyse'}<span className="evolis-dots"><i>.</i><i>.</i><i>.</i></span>
                        </div>
                    </div>
                )}

                {erreur && <div className="evolis-error">{erreur}</div>}
                <div ref={finRef} />
            </div>

            <div className="evolis-inputbar">
                <input
                    ref={inputRef}
                    type="text"
                    className="evolis-input"
                    placeholder="Pose ta question à Evolis…"
                    value={saisie}
                    maxLength={2000}
                    onChange={(e) => setSaisie(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !chargement) envoyer(); }}
                    disabled={chargement}
                />
                {chargement ? (
                    <button
                        type="button"
                        className="evolis-send evolis-stop"
                        onClick={arreter}
                        title="Arrêter"
                    >
                        <Square size={14} fill="currentColor" />
                    </button>
                ) : (
                    <button
                        type="button"
                        className="evolis-send"
                        onClick={() => envoyer()}
                        disabled={!saisie.trim()}
                        title="Envoyer"
                    >
                        <SendHorizonal size={17} />
                    </button>
                )}
            </div>
        </div>
    );
};

export default EvolisChat;
